var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import '../styles/footer.scss';
import { useContext, useEffect, useMemo, useState, useCallback } from 'react';
import ButtonGroup from './ButtonGroup';
import ToggleButton from './ToggleButton';
import CustomScrollbar from './CustomScrollbar';
import globalStore from '../store';
import NavBtn from './NavBtn';
import SettingWithPopup from './SettingWithPopup';
import { DataContext } from '../player/DataContext';
import customEventHandler from '../helper/customEvents';
import AudioButton from './AudioButton';
import useResizeObserver from '../helper/hooks/useResizeObserver';
import { useRovingTabIndex } from '../helper/hooks/useRovingTabIndex';
import { useTranslation } from 'react-i18next';
import { canUndo, canRedo } from '../helper/stateManagerHelper';
var Footer = function (props) {
    var _a, _b, _c, _d, _e, _f;
    var _g = useState(null), btndata = _g[0], setBtndata = _g[1];
    var setDisplayMode = globalStore.useStoreActions(function (state) { return state.playerStore; }).setDisplayMode;
    var _h = globalStore.useStoreState(function (state) { return state.playerStore; }), displayMode = _h.displayMode, viewEditMode = _h.viewEditMode, _j = _h.isLoggedIn, isLoggedIn = _j === void 0 ? true : _j, isIDM = _h.isIDM, isSwp = _h.isSwp, language = _h.language, playerIconZoom = _h.playerIconZoom;
    var mainConfig = globalStore.useStoreState(function (state) { return state.jsonDataStore; }).mainConfig;
    var _k = useContext(DataContext), setShades = _k.setShades, setShowEnterKeyModal = _k.setShowEnterKeyModal, setShowShareModal = _k.setShowShareModal, setShowSaveModal = _k.setShowSaveModal, showSaveModal = _k.showSaveModal, drawCommands = _k.drawCommands, annotations = _k.annotations, text = _k.text, table = _k.table, shades = _k.shades, manipulative = _k.manipulative;
    // Reset is enabled only when the whiteboard actually has content to clear —
    // shapes, manipulatives, counters, draw commands, text, tables, or shades.
    var hasWhiteboardContent = drawCommands.length > 0 ||
        annotations.length > 0 ||
        text.length > 0 ||
        table.length > 0 ||
        shades.length > 0 ||
        manipulative.length > 0;
    var elementRef = useResizeObserver().elementRef;
    var t = useTranslation().t;
    var toolChange = props.toolChange, collapsibleGuideProp = props.collapsibleGuide, disabledButtons = props.disabledButtons;
    // In view mode, hide 'edit' (configure) and 'lock' inside toolsCollapsible instead of hiding the entire toolbox
    var viewModeCollapsibleHidden = viewEditMode === 'view' ? ['edit', 'lock'] : [];
    var collapsibleGuide = useMemo(function () {
        if (viewModeCollapsibleHidden.length === 0)
            return collapsibleGuideProp;
        var existing = (collapsibleGuideProp === null || collapsibleGuideProp === void 0 ? void 0 : collapsibleGuideProp.toolsCollapsible) || {};
        var mergedHidden = Array.from(new Set(__spreadArray(__spreadArray([], (existing.hiddenElements || []), true), viewModeCollapsibleHidden, true)));
        return __assign(__assign({}, collapsibleGuideProp), { toolsCollapsible: __assign(__assign({}, existing), { hiddenElements: mergedHidden }) });
    }, [collapsibleGuideProp, viewEditMode]);
    // In view mode, only hide the entire toolsCollapsible if ALL its elements would be hidden
    var shareCode = globalStore.useStoreState(function (state) { return state.playerStore; }).shareCode;
    var hiddenButtons = useMemo(function () {
        var _a;
        var hidden = [];
        if (!isLoggedIn) {
            hidden.push('share', 'save');
        }
        if (isIDM || (!isSwp && !shareCode)) {
            hidden.push('share');
        }
        if (viewEditMode === 'view') {
            var toolsElements = ['edit', 'group', 'show5x5Grid', 'duplicate', 'lock', 'scale'];
            var hiddenInCollapsible_1 = ((_a = collapsibleGuide === null || collapsibleGuide === void 0 ? void 0 : collapsibleGuide.toolsCollapsible) === null || _a === void 0 ? void 0 : _a.hiddenElements) || [];
            var allHidden = toolsElements.every(function (el) { return hiddenInCollapsible_1.includes(el); });
            if (allHidden) {
                hidden.push('toolsCollapsible');
            }
        }
        return hidden;
    }, [viewEditMode, isLoggedIn, collapsibleGuide]);
    var _l = useState([]), undoRedoDisabled = _l[0], setUndoRedoDisabled = _l[1];
    var updateUndoRedoState = useCallback(function () {
        var disabled = [];
        if (!canUndo())
            disabled.push('undo');
        if (!canRedo())
            disabled.push('redo');
        // Reset is gated on whiteboard content, not on history. An empty
        // whiteboard with no shapes / manipulatives / draw commands has
        // nothing to reset even if undoable history exists.
        if (!hasWhiteboardContent)
            disabled.push('reset');
        setUndoRedoDisabled(disabled);
    }, [hasWhiteboardContent]);
    useEffect(function () {
        mainConfig && setBtndata(mainConfig.footerBtn);
    }, []);
    // Roving tabindex for 3 footer groups
    var mainBtnCount = 2; // SettingWithPopup + AudioButton
    var leftBtnCount = (_c = (_b = (_a = btndata === null || btndata === void 0 ? void 0 : btndata.footerRightBtns) === null || _a === void 0 ? void 0 : _a.part1) === null || _b === void 0 ? void 0 : _b.length) !== null && _c !== void 0 ? _c : 0;
    var rightBtnCount = (_f = (_e = (_d = btndata === null || btndata === void 0 ? void 0 : btndata.footerLeftBtns) === null || _d === void 0 ? void 0 : _d.part1) === null || _e === void 0 ? void 0 : _e.length) !== null && _f !== void 0 ? _f : 0;
    var mainRoving = useRovingTabIndex({ itemCount: mainBtnCount, orientation: 'horizontal' });
    var leftRoving = useRovingTabIndex({ itemCount: leftBtnCount, orientation: 'horizontal' });
    var rightRoving = useRovingTabIndex({ itemCount: rightBtnCount, orientation: 'horizontal' });
    useEffect(function () {
        updateUndoRedoState();
        customEventHandler.addEventListener('stateChanged', updateUndoRedoState);
        customEventHandler.addEventListener('headerClicked', updateUndoRedoState);
        return function () {
            customEventHandler.removeEventListener('stateChanged', updateUndoRedoState);
            customEventHandler.removeEventListener('headerClicked', updateUndoRedoState);
        };
    }, [updateUndoRedoState]);
    var mergedDisabledButtons = __spreadArray(__spreadArray([], (disabledButtons || []), true), undoRedoDisabled, true);
    var clickHandler = function (id, type, data) {
        switch (id) {
            case 'shade':
                setShades(function (prev) { return __spreadArray(__spreadArray([], prev, true), [
                    {
                        id: "id_".concat(Date.now()),
                        type: 'shade',
                        data: {
                            position: {
                                x: 0,
                                y: 0,
                                z: 0,
                            },
                            dimensions: {
                                width: 359,
                                height: 183,
                            },
                            revealed: false,
                        },
                    },
                ], false); });
                break;
            case 'keys':
                setShowEnterKeyModal(function (prev) { return !prev; });
                break;
            case 'share':
                setShowShareModal(function (prev) { return !prev; });
                break;
            case 'save':
                setShowSaveModal(function (prev) { return !prev; });
                break;
        }
    };
    return (_jsx(_Fragment, { children: btndata && (_jsxs("footer", { className: "footer", ref: elementRef, tabIndex: -1, "aria-label": t('text.mainToolbar'), children: [_jsx("div", { style: {
                        position: 'absolute',
                        width: '55%',
                        background: 'rgb(209 215 237)',
                        height: '100%',
                        left: '0',
                        zIndex: '-1',
                    } }), _jsx("div", { style: {
                        position: 'absolute',
                        width: '45%',
                        background: '#5063ae',
                        height: '100%',
                        right: '0',
                        zIndex: '-1',
                    } }), _jsx(ToggleButton, { className: "footerToggle", isParentRelative: true, toogleTooltipDirection: displayMode === 'smartBoard' && playerIconZoom >= 1.5 ? 'topRight' : 'top', initial: language === 'es' ? 'ES' : 'EN', onChange: function (lang) {
                        customEventHandler.dispatchEvent('headerClicked', { id: 'lang', data: lang });
                    } }), _jsxs("div", { className: "left-holder", style: {}, children: [_jsxs("div", __assign({ className: "main_btn_holder", role: "toolbar", "aria-label": t('text.utilities'), "aria-orientation": "horizontal" }, mainRoving.groupProps, { children: [_jsx("div", { ref: mainRoving.setItemRef(0), children: _jsx(SettingWithPopup, { hr: true }) }), _jsx("div", { ref: mainRoving.setItemRef(1), children: _jsx(AudioButton, { id: "footer" }) })] })), _jsx(CustomScrollbar, { className: "left_btn_holder_scroll", direction: "horizontal", style: { flex: '0 1 auto', minWidth: 0, width: 'auto', height: '100%' }, children: _jsx("div", { role: "toolbar", "aria-label": t('text.drawingTools'), "aria-orientation": "horizontal", ref: function (el) {
                                    leftRoving.groupProps.ref(el);
                                }, tabIndex: leftRoving.groupProps.tabIndex, onKeyDown: leftRoving.groupProps.onKeyDown, onFocus: leftRoving.groupProps.onFocus, children: _jsx(ButtonGroup, { align: "horizontal", buttonsInfo: btndata.footerRightBtns.part1, mainClass: "left_btn_holder", type: "footerBtn", isParentRelative: false, useViewportPosition: true, collapsible: {
                                        isTooltipRelative: false,
                                        isInnerTooltipRelative: true,
                                        // Viewport-relative coords (not parent-relative) so the
                                        // position:fixed flyout — which escapes the scrollbar's
                                        // overflow clip — lands at the button instead of jumping to
                                        // the viewport origin. Matches the sidebar .squareBox pattern.
                                        isRelative: false,
                                    }, onClick: function (id, type, data) {
                                        customEventHandler.dispatchEvent('headerClicked', { id: id, type: type, data: data });
                                        if (id === 'undo' || id === 'redo' || id === 'reload') {
                                            setTimeout(updateUndoRedoState, 0);
                                        }
                                    }, buttonStyleType: "imageType1", collapsibleGuide: collapsibleGuide, disabledButtons: mergedDisabledButtons, hiddenButtons: hiddenButtons, rovingGetTabIndex: leftRoving.getTabIndex, rovingSetItemRef: leftRoving.setItemRef, rovingStartIndex: 0 }) }) })] }), (isIDM || isSwp) && (_jsx("div", { className: "footer_nav_holder", children: _jsx(NavBtn, { class: "footerNav" }) })), _jsx("div", { className: "right-holder", role: "toolbar", "aria-label": t('text.fileActions'), "aria-orientation": "horizontal", ref: function (el) {
                        rightRoving.groupProps.ref(el);
                    }, tabIndex: rightRoving.groupProps.tabIndex, onKeyDown: rightRoving.groupProps.onKeyDown, onFocus: rightRoving.groupProps.onFocus, children: _jsx(CustomScrollbar, { className: "right_holder_scroll", direction: "horizontal", style: { flex: '0 1 auto', minWidth: 0, width: 'auto', height: '100%' }, children: _jsx(ButtonGroup, { align: "horizontal", buttonsInfo: btndata.footerLeftBtns.part1, mainClass: "footer-right-holder", type: "footerBtn", useViewportPosition: true, onClick: function (id, type, data) {
                                customEventHandler.dispatchEvent('headerClicked', { id: id, type: type, data: data });
                                if (id === 'undo' || id === 'redo' || id === 'reload') {
                                    setTimeout(updateUndoRedoState, 0);
                                }
                            }, buttonStyleType: "imageType1", collapsibleGuide: collapsibleGuide, disabledButtons: mergedDisabledButtons, rovingGetTabIndex: rightRoving.getTabIndex, rovingSetItemRef: rightRoving.setItemRef, rovingStartIndex: 0 }) }) })] })) }));
};
export default Footer;
